import { useCallback, useMemo, type CSSProperties } from 'react';
import Panel from '@/features/report/shared/Panel/Panel';
import type { FindingGroup, FlagGroup } from '@/models/template';
import { useGetWorksheetFindingClassesQuery } from '@/state/slices/options';
import panelStyles from '@/features/report/Findings/FindingsPanel.module.css';
import { EditableFlagsBar } from './EditableFlagsBar';
import { EditableFindingGroup } from './EditableFindingGroup';

interface EditableFindingsSectionProps {
  findingGroups: FindingGroup[];
  flagGroups: FlagGroup[];
  title?: string;
  onFindingGroupsChange: (groups: FindingGroup[]) => void;
  onFlagGroupsChange: (groups: FlagGroup[]) => void;
}

export function EditableFindingsSection({
  findingGroups,
  flagGroups,
  title = 'Findings',
  onFindingGroupsChange,
  onFlagGroupsChange,
}: EditableFindingsSectionProps) {
  const { data: findingClasses = [] } = useGetWorksheetFindingClassesQuery();

  // The API does not order groups; `index` does, as in the sidebar.
  const ordered = useMemo(
    () => [...findingGroups].sort((a, b) => (a.index ?? 0) - (b.index ?? 0)),
    [findingGroups]
  );

  const usedClasses = useMemo(() => {
    const used = new Set<string>();
    ordered.forEach((g) =>
      g.findingLabels.forEach((fl) => {
        if (fl.findingClass) used.add(fl.findingClass);
      })
    );
    return used;
  }, [ordered]);

  const handleGroupChange = useCallback(
    (idx: number, next: FindingGroup) => {
      const copy = [...ordered];
      copy[idx] = next;
      onFindingGroupsChange(copy);
    },
    [ordered, onFindingGroupsChange]
  );

  const renderGroup = (group: FindingGroup) => {
    const idx = ordered.indexOf(group);
    return (
      <EditableFindingGroup
        key={group.uuid}
        group={group}
        position={idx + 1}
        findingClasses={findingClasses}
        usedClasses={usedClasses}
        onChange={(next) => handleGroupChange(idx, next)}
      />
    );
  };

  return (
    <Panel title={title}>
      <div className={panelStyles.header}>
        <div className={panelStyles.controls}>
          <EditableFlagsBar
            flagGroups={flagGroups}
            onChange={onFlagGroupsChange}
          />
        </div>
      </div>

      <div className={panelStyles.list}>
        <div
          className={panelStyles.grid}
          // Same column-first grid as the reader's FindingsPanel.
          style={
            { '--finding-rows': Math.ceil(ordered.length / 2) } as CSSProperties
          }
        >
          {ordered.map(renderGroup)}
        </div>
      </div>
    </Panel>
  );
}
